import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";

import {
  AssetDistributionQueryParams,
  ClientResponse,
  IQueryPerams,
} from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import {
  AssetDistributionStatusType,
  assetDistributionStatusList,
} from "../../config/constants/common";

import { formatSnakeCaseToReadable } from "../../utils/common";
import { AssetCategorySelectInput } from "../select-input-models/AssetCategorySelectInput";
import { Field } from "../../utils/formSchema";
import ItemSelectInput from "../../components/Input/ItemSelectInput";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T) => void;
  isDeleted?: boolean;
  isAssigned?: boolean;
  distribution_status?: AssetDistributionStatusType;
}

interface Ifilter {
  batch_number: string;
  model: string;
  asset_status: string;
  asset_category_name: string;
  is_assigned?: boolean;
  is_deleted?: boolean;
  brand: string;
  date_received: string;
  distribution_status?: AssetDistributionStatusType;
}
const assetDistributionStatus = assetDistributionStatusList.map((item) => ({
  label: item,
  value: item,
}));

export const DistributedAssetsLogsFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<AssetDistributionQueryParams>({
      asset_category_name: "",
      asset_brand_name: "",
      asset_distribution_status: undefined,
    });
  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
      const state = {
        ...p,
        [key]: { value: "" },
      };
      updateQuerySet((prev) => ({ ...prev, [key]: "" }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    updateQuerySet(data);
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.asset_brand_name?.value,
    form.asset_category_name?.value,
    form.asset_distribution_status?.value,
  ]);

  function handleAssetCatSelect(
    item: Field,
    isPhysical?: boolean,
    label?: string
  ) {
    updateForm((p) => ({ ...p, asset_category_name: { value: label ?? "" } }));
  }
  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      width={"100%"}
      mb={1}
    >
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading}>
          <Stack direction={"column"} spacing={2}>
            <ItemSelectInput
              size="full"
              label={"Asset Distribution Status"}
              item={""}
              placeholder={"Search by asset distribution status ..."}
              items={assetDistributionStatus}
              handleChange={updateInputField("asset_distribution_status")}
              value={form.asset_distribution_status?.value ?? ""}
            />
            <AssetCategorySelectInput
              placeholder={"Select Asset Category"}
              label={"Asset Category"}
              handleChange={handleAssetCatSelect}
              size="full"
            />
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as keyof Ifilter)}
              />
            ))}
        </Box>
      </Stack>
      <Box width={250}>
        <SideBarSearchInput
          placeholder="Search by brand name..."
          value={form.asset_brand_name?.value}
          handleChange={updateInputField("asset_brand_name")}
        />
      </Box>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570,
    },
  }),
};
